import type { NextPage } from "next"
import clsx from "clsx"

import { DocumentType } from "~/api/prismic/enums"
import { linkResolver } from "~/api/prismic/linkResolver"
import CustomRichText from "~/components/CustomRichText"
import LazyImage from "~/components/LazyImage"
import { ScrollProgressBar } from "~/components/ScrollProgressBar"
import { SEO } from "~/components/SEO"
import type { ArticleSliceZoneProps } from "~/screens/ArticlePage/ArticleSliceZone"
import ArticleSliceZone from "~/screens/ArticlePage/ArticleSliceZone"
import Footnotes from "~/screens/ArticlePage/Footnotes"
import type { MaybeImage } from "~/types/images"
import { imageOrFallback } from "~/utils/images/imageOrFallback"
import { getClassesByColorScheme } from "~/utils/style/colorSchemes/getClassesByColorScheme"

import type { FootnotesProviderProps } from "./Footnotes/context"
import { FootnotesProvider } from "./Footnotes/context"
import type { AuthorDetailsProps } from "./AuthorDetails"
import { AuthorDetails } from "./AuthorDetails"
import type { ContributeSectionProps } from "./ContributeSection"
import { ContributeSection } from "./ContributeSection"
import type { RelatedArticlesProps } from "./RelatedArticles"
import { RelatedArticles } from "./RelatedArticles"
import type { StickyBarProps } from "./StickyBar"
import { StickyBar } from "./StickyBar"

export type ArticlePathParams = {
  uid: string
}

export type ArticleProps = ArticlePathParams & {
  title: string
  subtitle: prismic.RichTextField
  meta: {
    description: string
  }
  bannerImage: MaybeImage
  bannerImageCredits: string
} & ArticleSliceZoneProps &
  AuthorDetailsProps &
  ContributeSectionProps &
  FootnotesProviderProps &
  RelatedArticlesProps &
  Omit<StickyBarProps, "articleUID">

export const ArticlePage: NextPage<ArticleProps> = ({
  uid,
  date,
  articleId,
  categoryIds,
  audioURL,
  meta,
  path,
  bannerImage,
  bannerImageCredits,
  authors,
  slices,
  title,
  footnotes,
  subtitle,
  colorSchemeId,
}) => {
  const bannerClasses = getClassesByColorScheme({
    colorScheme: { uid: colorSchemeId },
  })

  return (
    <FootnotesProvider footnotes={footnotes}>
      <article>
        <SEO
          pageTitle={title}
          description={meta.description || undefined}
          path={linkResolver({ uid, type: DocumentType.ARTICLE })}
          image={bannerImage}
          twitter={{ cardType: "summary_large_image" }}
          type="article"
        />
        <ScrollProgressBar key={uid} />
        <div className="mx-auto max-w-screen-lg">
          <div className="relative flex flex-col justify-end py-1/2 md:pt-3/5 md:pb-0">
            <LazyImage
              className="absolute top-0 left-0 h-full w-full object-cover"
              src={imageOrFallback(bannerImage?.url)}
              alt=""
            />
            <header className="absolute bottom-0 left-0 z-0 flex flex-col items-start">
              <h1
                className={clsx(
                  "inline-flex",
                  "mt-16 py-2 px-4",
                  "text-xl sm:text-2xl md:text-3xl",
                  bannerClasses.bgContrast,
                  bannerClasses.text,
                )}
              >
                {title}
              </h1>
            </header>
          </div>
          <div
            className={clsx(
              "flex flex-col space-y-4",
              "p-4 font-medium md:text-xl",
              bannerClasses.text,
              bannerClasses.bg,
            )}
          >
            <CustomRichText field={subtitle} />
            <div className="space-x-2 text-sm md:text-base">
              <AuthorDetails authors={authors} date={date} />
            </div>
            {!!bannerImageCredits.length && (
              <div
                className="banner-image-credit text-sm italic"
                dangerouslySetInnerHTML={{ __html: bannerImageCredits }}
              />
            )}
          </div>
        </div>
        <StickyBar
          articleUID={uid}
          audioURL={audioURL}
          title={title}
          path={path}
        />
        <div id="article-slice-zone">
          <ArticleSliceZone slices={slices} />
        </div>
        <ContributeSection colorSchemeId={colorSchemeId} />
        <Footnotes />
      </article>
      <RelatedArticles articleId={articleId} categoryIds={categoryIds} />
    </FootnotesProvider>
  )
}
